import React, { useState, useEffect } from 'react';
import {
  Card,
  Text,
  Group,
  Stack,
  Badge,
  Table,
  Button,
  Modal,
  TextInput,
  Textarea,
  Select,
  NumberInput,
  MultiSelect,
  JsonInput,
  Switch,
  ActionIcon,
  Loader,
  Alert,
  Tabs,
  Accordion,
  Code,
  Grid,
  Paper,
} from '@mantine/core';
import {
  IconUser,
  IconPlus,
  IconEdit,
  IconTrash,
  IconEye,
  IconHistory,
  IconSettings,
  IconShield,
  IconClock,
  IconRefresh,
  IconAlertCircle,
  IconCheck,
} from '@tabler/icons-react';
import {
  useActorDescriptor,
  type ActorDescriptor,
  type CreateActorDescriptorInput,
  type UpdateActorDescriptorInput,
  type ActorType,
  type RetryPolicy,
} from '../hooks/useActorDescriptor';

export const ActorDescriptorManager: React.FC = () => {
  const {
    actors,
    selectedActor,
    loading,
    listActors,
    getActor,
    createActor,
    updateActor,
    deleteActor,
    listActorVersions,
    setSelectedActor,
  } = useActorDescriptor();

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [versionsModalOpen, setVersionsModalOpen] = useState(false);
  const [actorVersions, setActorVersions] = useState<ActorDescriptor[]>([]);

  // Form state
  const [formName, setFormName] = useState('');
  const [formActorType, setFormActorType] = useState<ActorType>('custom');
  const [formVersion, setFormVersion] = useState('1.0.0');
  const [formDescription, setFormDescription] = useState('');
  const [formCapabilities, setFormCapabilities] = useState<string[]>([]);
  const [formPermissions, setFormPermissions] = useState<string[]>([]);
  const [formConfiguration, setFormConfiguration] = useState('{}');
  const [formTimeoutSeconds, setFormTimeoutSeconds] = useState<number>(60);
  const [formRetryEnabled, setFormRetryEnabled] = useState(false);
  const [formMaxAttempts, setFormMaxAttempts] = useState(3);
  const [formBackoffSeconds, setFormBackoffSeconds] = useState(1);
  const [formBackoffMultiplier, setFormBackoffMultiplier] = useState(2.0);
  const [formMaxBackoffSeconds, setFormMaxBackoffSeconds] = useState(60);
  const [formActive, setFormActive] = useState(true);

  // Filter state
  const [filterActorType, setFilterActorType] = useState<ActorType | undefined>(undefined);
  const [filterActive, setFilterActive] = useState<boolean | undefined>(undefined);
  const [filterNamePattern, setFilterNamePattern] = useState('');

  // ============================================================================
  // Apply Filters
  // ============================================================================

  const handleApplyFilters = () => {
    listActors({
      actor_type: filterActorType,
      active: filterActive,
      name_pattern: filterNamePattern || undefined,
    });
  };

  // ============================================================================
  // Reset Form
  // ============================================================================

  const resetForm = () => {
    setFormName('');
    setFormActorType('custom');
    setFormVersion('1.0.0');
    setFormDescription('');
    setFormCapabilities([]);
    setFormPermissions([]);
    setFormConfiguration('{}');
    setFormTimeoutSeconds(60);
    setFormRetryEnabled(false);
    setFormMaxAttempts(3);
    setFormBackoffSeconds(1);
    setFormBackoffMultiplier(2.0);
    setFormMaxBackoffSeconds(60);
    setFormActive(true);
  };

  // ============================================================================
  // Load Actor for Edit
  // ============================================================================

  const loadActorForEdit = (actor: ActorDescriptor) => {
    setFormName(actor.name);
    setFormActorType(actor.actor_type);
    setFormVersion(actor.version);
    setFormDescription(actor.description || '');
    setFormCapabilities(actor.capabilities);
    setFormPermissions(actor.required_permissions);
    setFormConfiguration(JSON.stringify(actor.configuration, null, 2));
    setFormTimeoutSeconds(actor.timeout_seconds || 60);
    setFormRetryEnabled(!!actor.retry_policy);
    if (actor.retry_policy) {
      setFormMaxAttempts(actor.retry_policy.max_attempts);
      setFormBackoffSeconds(actor.retry_policy.backoff_seconds);
      setFormBackoffMultiplier(actor.retry_policy.backoff_multiplier);
      setFormMaxBackoffSeconds(actor.retry_policy.max_backoff_seconds);
    }
    setFormActive(actor.active);
    setSelectedActor(actor);
    setEditModalOpen(true);
  };

  // ============================================================================
  // Create Actor Handler
  // ============================================================================

  const handleCreateActor = async () => {
    let configuration: Record<string, any> = {};
    try {
      configuration = JSON.parse(formConfiguration);
    } catch (error) {
      return;
    }

    const input: CreateActorDescriptorInput = {
      name: formName,
      actor_type: formActorType,
      version: formVersion,
      description: formDescription || undefined,
      capabilities: formCapabilities,
      required_permissions: formPermissions,
      configuration,
      timeout_seconds: formTimeoutSeconds,
      retry_policy: formRetryEnabled
        ? {
            max_attempts: formMaxAttempts,
            backoff_seconds: formBackoffSeconds,
            backoff_multiplier: formBackoffMultiplier,
            max_backoff_seconds: formMaxBackoffSeconds,
          }
        : undefined,
    };

    const result = await createActor(input);
    if (result) {
      setCreateModalOpen(false);
      resetForm();
    }
  };

  // ============================================================================
  // Update Actor Handler
  // ============================================================================

  const handleUpdateActor = async () => {
    if (!selectedActor) return;

    let configuration: Record<string, any> = {};
    try {
      configuration = JSON.parse(formConfiguration);
    } catch (error) {
      return;
    }

    const input: UpdateActorDescriptorInput = {
      id: selectedActor.id,
      description: formDescription || undefined,
      capabilities: formCapabilities,
      configuration,
      timeout_seconds: formTimeoutSeconds,
      retry_policy: formRetryEnabled
        ? {
            max_attempts: formMaxAttempts,
            backoff_seconds: formBackoffSeconds,
            backoff_multiplier: formBackoffMultiplier,
            max_backoff_seconds: formMaxBackoffSeconds,
          }
        : undefined,
      active: formActive,
    };

    const result = await updateActor(input);
    if (result) {
      setEditModalOpen(false);
      resetForm();
    }
  };

  // ============================================================================
  // Delete Actor Handler
  // ============================================================================

  const handleDeleteActor = async () => {
    if (!selectedActor) return;

    const success = await deleteActor(selectedActor.id);
    if (success) {
      setDeleteModalOpen(false);
      setSelectedActor(null);
    }
  };

  // ============================================================================
  // Load Actor Versions
  // ============================================================================

  const handleLoadVersions = async (name: string) => {
    const versions = await listActorVersions(name);
    setActorVersions(versions);
    setVersionsModalOpen(true);
  };

  // ============================================================================
  // Helper Functions
  // ============================================================================

  const getActorTypeColor = (type: ActorType): string => {
    switch (type) {
      case 'system':
        return 'blue';
      case 'custom':
        return 'green';
      case 'external':
        return 'orange';
      case 'composite':
        return 'purple';
    }
  };

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <Card shadow="sm" padding="lg">
      <Stack gap="md">
        <Group justify="space-between">
          <Group gap="xs">
            <IconUser size={24} />
            <Text size="xl" fw={700}>
              Actor Descriptor Manager
            </Text>
          </Group>
          <Button leftSection={<IconPlus size={16} />} onClick={() => setCreateModalOpen(true)}>
            Create Actor
          </Button>
        </Group>

        {/* Filters */}
        <Card shadow="sm" padding="md" withBorder>
          <Grid>
            <Grid.Col span={3}>
              <Select
                label="Actor Type"
                placeholder="All types"
                value={filterActorType}
                onChange={(value) => setFilterActorType(value as ActorType | undefined)}
                data={[
                  { value: 'system', label: 'System' },
                  { value: 'custom', label: 'Custom' },
                  { value: 'external', label: 'External' },
                  { value: 'composite', label: 'Composite' },
                ]}
                clearable
              />
            </Grid.Col>
            <Grid.Col span={3}>
              <Select
                label="Status"
                placeholder="All statuses"
                value={filterActive === undefined ? undefined : filterActive ? 'active' : 'inactive'}
                onChange={(value) =>
                  setFilterActive(value === 'active' ? true : value === 'inactive' ? false : undefined)
                }
                data={[
                  { value: 'active', label: 'Active' },
                  { value: 'inactive', label: 'Inactive' },
                ]}
                clearable
              />
            </Grid.Col>
            <Grid.Col span={4}>
              <TextInput
                label="Name Pattern"
                placeholder="Search by name"
                value={filterNamePattern}
                onChange={(e) => setFilterNamePattern(e.currentTarget.value)}
              />
            </Grid.Col>
            <Grid.Col span={2}>
              <Button fullWidth mt={24} onClick={handleApplyFilters} leftSection={<IconRefresh size={16} />}>
                Apply Filters
              </Button>
            </Grid.Col>
          </Grid>
        </Card>

        {/* Actors Table */}
        {loading && actors.length === 0 ? (
          <Group justify="center" py={40}>
            <Loader />
          </Group>
        ) : actors.length > 0 ? (
          <Table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Version</th>
                <th>Capabilities</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {actors.map((actor) => (
                <tr key={actor.id}>
                  <td>
                    <Text fw={600}>{actor.name}</Text>
                    {actor.description && (
                      <Text size="xs" color="dimmed">
                        {actor.description}
                      </Text>
                    )}
                  </td>
                  <td>
                    <Badge color={getActorTypeColor(actor.actor_type)}>{actor.actor_type}</Badge>
                  </td>
                  <td>
                    <Text size="sm">{actor.version}</Text>
                  </td>
                  <td>
                    <Group gap="xs">
                      {actor.capabilities.slice(0, 2).map((cap, idx) => (
                        <Badge key={idx} size="sm" variant="outline">
                          {cap}
                        </Badge>
                      ))}
                      {actor.capabilities.length > 2 && (
                        <Badge size="sm" variant="outline">
                          +{actor.capabilities.length - 2}
                        </Badge>
                      )}
                    </Group>
                  </td>
                  <td>
                    {actor.active ? (
                      <Badge color="green" leftSection={<IconCheck size={12} />}>
                        Active
                      </Badge>
                    ) : (
                      <Badge color="gray">Inactive</Badge>
                    )}
                  </td>
                  <td>
                    <Group gap="xs">
                      <ActionIcon
                        variant="light"
                        onClick={() => {
                          setSelectedActor(actor);
                          setDetailsModalOpen(true);
                        }}
                      >
                        <IconEye size={16} />
                      </ActionIcon>
                      <ActionIcon variant="light" color="blue" onClick={() => loadActorForEdit(actor)}>
                        <IconEdit size={16} />
                      </ActionIcon>
                      <ActionIcon variant="light" color="orange" onClick={() => handleLoadVersions(actor.name)}>
                        <IconHistory size={16} />
                      </ActionIcon>
                      <ActionIcon
                        variant="light"
                        color="red"
                        onClick={() => {
                          setSelectedActor(actor);
                          setDeleteModalOpen(true);
                        }}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Group>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <Text c="dimmed" ta="center" py={40}>
            No actors found
          </Text>
        )}

        {/* Create Modal */}
        <Modal
          opened={createModalOpen}
          onClose={() => {
            setCreateModalOpen(false);
            resetForm();
          }}
          title={
            <Group gap="xs">
              <IconPlus size={20} />
              <Text fw={600}>Create Actor Descriptor</Text>
            </Group>
          }
          size="lg"
        >
          <Stack gap="md">
            <TextInput
              label="Actor Name"
              placeholder="e.g., order-processor"
              value={formName}
              onChange={(e) => setFormName(e.currentTarget.value)}
              required
            />

            <Select
              label="Actor Type"
              value={formActorType}
              onChange={(value) => setFormActorType(value as ActorType)}
              data={[
                { value: 'system', label: 'System' },
                { value: 'custom', label: 'Custom' },
                { value: 'external', label: 'External' },
                { value: 'composite', label: 'Composite' },
              ]}
              required
            />

            <TextInput label="Version" value={formVersion} onChange={(e) => setFormVersion(e.currentTarget.value)} required />

            <Textarea label="Description" value={formDescription} onChange={(e) => setFormDescription(e.currentTarget.value)} minRows={2} />

            <MultiSelect
              label="Capabilities"
              placeholder="Add capabilities"
              value={formCapabilities}
              onChange={setFormCapabilities}
              data={formCapabilities}
              searchable
            />

            <MultiSelect
              label="Required Permissions"
              placeholder="Add permissions"
              value={formPermissions}
              onChange={setFormPermissions}
              data={formPermissions}
              searchable
            />

            <JsonInput label="Configuration (JSON)" value={formConfiguration} onChange={setFormConfiguration} minRows={4} formatOnBlur />

            <NumberInput label="Timeout (seconds)" value={formTimeoutSeconds} onChange={(value) => setFormTimeoutSeconds(Number(value) || 60)} min={1} />

            <Accordion variant="contained">
              <Accordion.Item value="retry-policy">
                <Accordion.Control icon={<IconRefresh size={16} />}>Retry Policy (Optional)</Accordion.Control>
                <Accordion.Panel>
                  <Stack gap="md">
                    <Switch label="Enable Retry Policy" checked={formRetryEnabled} onChange={(e) => setFormRetryEnabled(e.currentTarget.checked)} />

                    {formRetryEnabled && (
                      <>
                        <NumberInput label="Max Attempts" value={formMaxAttempts} onChange={(value) => setFormMaxAttempts(Number(value) || 3)} min={1} max={10} />

                        <NumberInput
                          label="Initial Backoff (seconds)"
                          value={formBackoffSeconds}
                          onChange={(value) => setFormBackoffSeconds(Number(value) || 1)}
                          min={1}
                        />

                        <NumberInput
                          label="Backoff Multiplier"
                          value={formBackoffMultiplier}
                          onChange={(value) => setFormBackoffMultiplier(Number(value) || 2.0)}
                          min={1}
                          step={0.1}
                          decimalScale={1}
                        />

                        <NumberInput
                          label="Max Backoff (seconds)"
                          value={formMaxBackoffSeconds}
                          onChange={(value) => setFormMaxBackoffSeconds(Number(value) || 60)}
                          min={1}
                        />
                      </>
                    )}
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>

            <Group justify="flex-end" mt="md">
              <Button
                variant="outline"
                onClick={() => {
                  setCreateModalOpen(false);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button onClick={handleCreateActor} loading={loading} disabled={!formName || !formVersion}>
                Create Actor
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Edit Modal - Similar structure to Create Modal */}
        <Modal
          opened={editModalOpen}
          onClose={() => {
            setEditModalOpen(false);
            resetForm();
          }}
          title={
            <Group gap="xs">
              <IconEdit size={20} />
              <Text fw={600}>Edit Actor Descriptor</Text>
            </Group>
          }
          size="lg"
        >
          <Stack gap="md">
            <Alert icon={<IconAlertCircle size={16} />} color="blue">
              <Text size="sm">Name, type, and version cannot be changed. Create a new version instead.</Text>
            </Alert>

            <TextInput label="Actor Name" value={formName} disabled />

            <Select label="Actor Type" value={formActorType} disabled data={[]} />

            <TextInput label="Version" value={formVersion} disabled />

            <Textarea label="Description" value={formDescription} onChange={(e) => setFormDescription(e.currentTarget.value)} minRows={2} />

            <MultiSelect
              label="Capabilities"
              placeholder="Add capabilities"
              value={formCapabilities}
              onChange={setFormCapabilities}
              data={formCapabilities}
              searchable
            />

            <JsonInput label="Configuration (JSON)" value={formConfiguration} onChange={setFormConfiguration} minRows={4} formatOnBlur />

            <NumberInput label="Timeout (seconds)" value={formTimeoutSeconds} onChange={(value) => setFormTimeoutSeconds(Number(value) || 60)} min={1} />

            <Accordion variant="contained">
              <Accordion.Item value="retry-policy">
                <Accordion.Control icon={<IconRefresh size={16} />}>Retry Policy</Accordion.Control>
                <Accordion.Panel>
                  <Stack gap="md">
                    <Switch label="Enable Retry Policy" checked={formRetryEnabled} onChange={(e) => setFormRetryEnabled(e.currentTarget.checked)} />

                    {formRetryEnabled && (
                      <>
                        <NumberInput label="Max Attempts" value={formMaxAttempts} onChange={(value) => setFormMaxAttempts(Number(value) || 3)} min={1} max={10} />

                        <NumberInput
                          label="Initial Backoff (seconds)"
                          value={formBackoffSeconds}
                          onChange={(value) => setFormBackoffSeconds(Number(value) || 1)}
                          min={1}
                        />

                        <NumberInput
                          label="Backoff Multiplier"
                          value={formBackoffMultiplier}
                          onChange={(value) => setFormBackoffMultiplier(Number(value) || 2.0)}
                          min={1}
                          step={0.1}
                          decimalScale={1}
                        />

                        <NumberInput
                          label="Max Backoff (seconds)"
                          value={formMaxBackoffSeconds}
                          onChange={(value) => setFormMaxBackoffSeconds(Number(value) || 60)}
                          min={1}
                        />
                      </>
                    )}
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>

            <Switch label="Active" checked={formActive} onChange={(e) => setFormActive(e.currentTarget.checked)} />

            <Group justify="flex-end" mt="md">
              <Button
                variant="outline"
                onClick={() => {
                  setEditModalOpen(false);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button onClick={handleUpdateActor} loading={loading}>
                Update Actor
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Details Modal */}
        <Modal
          opened={detailsModalOpen}
          onClose={() => setDetailsModalOpen(false)}
          title={
            <Group gap="xs">
              <IconEye size={20} />
              <Text fw={600}>Actor Details</Text>
            </Group>
          }
          size="lg"
        >
          {selectedActor && (
            <Stack gap="md">
              <Grid>
                <Grid.Col span={6}>
                  <Paper shadow="sm" p="md" withBorder>
                    <Text size="sm" color="dimmed">
                      Name
                    </Text>
                    <Text fw={600}>{selectedActor.name}</Text>
                  </Paper>
                </Grid.Col>
                <Grid.Col span={6}>
                  <Paper shadow="sm" p="md" withBorder>
                    <Text size="sm" color="dimmed">
                      Type
                    </Text>
                    <Badge color={getActorTypeColor(selectedActor.actor_type)}>{selectedActor.actor_type}</Badge>
                  </Paper>
                </Grid.Col>
              </Grid>

              {selectedActor.description && (
                <>
                  <Text size="sm" color="dimmed">
                    Description
                  </Text>
                  <Text size="sm">{selectedActor.description}</Text>
                </>
              )}

              <Text size="sm" fw={600} mt="md">
                Capabilities
              </Text>
              <Group gap="xs">
                {selectedActor.capabilities.map((cap, idx) => (
                  <Badge key={idx} variant="outline">
                    {cap}
                  </Badge>
                ))}
              </Group>

              <Text size="sm" fw={600} mt="md">
                Required Permissions
              </Text>
              <Group gap="xs">
                {selectedActor.required_permissions.map((perm, idx) => (
                  <Badge key={idx} variant="outline" leftSection={<IconShield size={12} />}>
                    {perm}
                  </Badge>
                ))}
              </Group>

              <Text size="sm" fw={600} mt="md">
                Configuration
              </Text>
              <Code block>{JSON.stringify(selectedActor.configuration, null, 2)}</Code>

              {selectedActor.retry_policy && (
                <>
                  <Text size="sm" fw={600} mt="md">
                    Retry Policy
                  </Text>
                  <Code block>{JSON.stringify(selectedActor.retry_policy, null, 2)}</Code>
                </>
              )}
            </Stack>
          )}
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal
          opened={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          title={
            <Group gap="xs">
              <IconTrash size={20} />
              <Text fw={600}>Delete Actor</Text>
            </Group>
          }
          size="md"
        >
          <Stack gap="md">
            <Alert icon={<IconAlertCircle size={16} />} color="red">
              <Text size="sm">
                Are you sure you want to delete actor &quot;{selectedActor?.name}&quot;? This action cannot be undone.
              </Text>
            </Alert>

            <Group justify="flex-end" mt="md">
              <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>
                Cancel
              </Button>
              <Button color="red" leftSection={<IconTrash size={16} />} onClick={handleDeleteActor} loading={loading}>
                Delete Actor
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Versions Modal */}
        <Modal
          opened={versionsModalOpen}
          onClose={() => setVersionsModalOpen(false)}
          title={
            <Group gap="xs">
              <IconHistory size={20} />
              <Text fw={600}>Actor Versions</Text>
            </Group>
          }
          size="lg"
        >
          <Stack gap="md">
            {actorVersions.length > 0 ? (
              <Table>
                <thead>
                  <tr>
                    <th>Version</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Created By</th>
                  </tr>
                </thead>
                <tbody>
                  {actorVersions.map((version) => (
                    <tr key={version.id}>
                      <td>
                        <Text fw={600}>{version.version}</Text>
                      </td>
                      <td>
                        {version.active ? (
                          <Badge color="green">Active</Badge>
                        ) : (
                          <Badge color="gray">Inactive</Badge>
                        )}
                      </td>
                      <td>{new Date(version.created_at).toLocaleDateString()}</td>
                      <td>{version.created_by}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <Text c="dimmed" ta="center" py={20}>
                No versions found
              </Text>
            )}
          </Stack>
        </Modal>
      </Stack>
    </Card>
  );
};
